import React, { FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { useTranslation } from 'react-i18next';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { SectionWrapper } from '../SectionWrapper';
import { FormInput } from '../merchants/FormInput';
import { isEqual } from 'lodash';
import { errorHandler } from '../../utils/newApplicationUtils';
import { PARTNER_BANK_INPUTS } from '../../constants/new-partner/partner-bank-inputs';
import { useParams } from 'react-router-dom';
import {
	convertPartnerBankFieldsToInitial, getFormattedPartnerBankData, getFormattedPartnerBankFormFields,
	partnerBankInitialValues,
} from '../../constants/new-partner/partner-bank-form';
import { fetchPartnersFees } from '../../store/merchnts/actions/partners';
import { ButtonComponent } from '../ButtonComponent';
import { usePaymentContext } from '../../store/payments/payments-context';
import {
	changePartnerBankDetails,
	fetchPartnerBankDetails,
	setPartnerBankDetails, setPartnerBankVerification,
} from '../../store/payments/actions/partners';

export const PartnerDetailsBank: FC = () => {
	const { t } = useTranslation();
	const { partnerId } = useParams();
	const { paymentContextState, dispatch: paymentDispatch } = usePaymentContext();
	const { partnerBankDetails, isPartnerBankDetailsLoading, isPartnerBankVerificationLoading } = paymentContextState;
	const { merchantState: { partnerDetailsData, merchantsLists: lists }, dispatch } = useMerchants();

	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
	  isOpen: false
	});
	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const formik = useFormik({
		initialValues: partnerBankInitialValues,
		onSubmit: () => void 0,
	});

	useEffect(() => {
		if (partnerBankDetails) {
			const convertedData = convertPartnerBankFieldsToInitial(partnerBankDetails);
			formik.setValues(convertedData);
		}
	}, [partnerBankDetails]);

	const handleSaveClick = async (): Promise<void> => {
		formik.handleSubmit();
		if (formik.isValid) {
			try {
				const initialUserData = getFormattedPartnerBankData(partnerBankDetails);
				const changedData = getFormattedPartnerBankFormFields(formik.values);
				if (!isEqual(changedData, initialUserData)) {
					setIsLoading(true);
					if (partnerBankDetails) {
						await changePartnerBankDetails({ formik, financialAccountId: partnerDetailsData?.financialAccountId });
						await fetchPartnerBankDetails({ dispatch: paymentDispatch, financialAccountId: partnerDetailsData?.financialAccountId });
					} else {
						await setPartnerBankDetails({ formik, pid: partnerId });
						await fetchPartnersFees(dispatch, partnerId);
					}
					setIsLoading(false);
				}
				setIsEditMode(!isEditMode);
			} catch (e) {
				console.log(e);
				errorHandler({ e, formik });
				setIsLoading(false);
			}
		}
	};

	const handleCancelClick = (): void => {
		const initialUserData = getFormattedPartnerBankData(partnerBankDetails);
		const changedData = getFormattedPartnerBankFormFields(formik.values);
		const initialValues = convertPartnerBankFieldsToInitial(partnerBankDetails);
		if (!isEqual(changedData, initialUserData)) {
			setModalState({
        isOpen: true,
        modalTitle: t('MERCHANT.CANCEL_MODAL_TITLE'),
        modalText: t('MERCHANT.CANCEL_MODAL_TEXT'),
        confirmButtonText: t('MERCHANT.CANCEL_MODAL_CONFIRM_BTN_NAME'),
        onConfirm: closeModal,
        onCancel: (): void => {
          closeModal();
          setIsEditMode(false);
          formik.setValues(initialValues);
        }
      });
		} else {
			setIsEditMode(false);
		}
	};

	const INPUTS = PARTNER_BANK_INPUTS({ lists, t });

	return (
		<>
			<SectionWrapper
				title={t('PARTNERS.PARTNER_BANK')}
				additionalLabel={partnerBankDetails?.verificationState}
				sectionAdditionalClass="min-height-200 mb-5"
				isEditMode={isEditMode}
				setIsEditMode={setIsEditMode}
				onEditClick={handleSaveClick}
				onCancelClick={handleCancelClick}
				pageLoading={isPartnerBankDetailsLoading}
				loading={isLoading}
				isCardBody
				isEditBlock
			>
				<div className="row">
					{
						INPUTS.map(item => (
							<FormInput
								{...item}
								formik={formik}
								key={item.key}
								isEditMode={isEditMode}
							/>
						))
					}
				</div>
			</SectionWrapper>

			<ButtonComponent
				buttonName="Verify account"
				onButtonClick={(): Promise<void> => setPartnerBankVerification({ dispatch: paymentDispatch, financialAccountId: partnerDetailsData?.financialAccountId })}
				additionalClass="d-block ms-auto w-200px"
				isDisabled={!partnerDetailsData?.financialAccountId || isPartnerBankVerificationLoading}
				loading={isPartnerBankVerificationLoading}
			/>

			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
		</>
	);
};